import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState('');
  const navigate = useNavigate();

  const sections = [
    { id: 'introduction', title: 'Introduction', icon: '🔒' },
    { id: 'information-collect', title: 'Information We Collect', icon: '📊' },
    { id: 'how-we-use', title: 'How We Use Your Information', icon: '🚀' },
    { id: 'contact', title: 'Contact Us', icon: '📮' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;
      
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const elementTop = element.offsetTop;
          const elementBottom = elementTop + element.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition < elementBottom) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <Navbar />

      <div className="pt-20 sm:pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Table of Contents */}
            <div className="lg:w-64 lg:flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                    <span className="mr-2">📋</span>
                    <span className="hidden sm:inline">Table of Contents</span>
                    <span className="sm:hidden">Contents</span>
                  </h3>
                  <nav className="grid grid-cols-2 lg:grid-cols-1 gap-2 lg:space-y-0">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm transition-all duration-200 flex items-center ${
                          activeSection === section.id
                            ? 'bg-purple-100 text-purple-700 font-medium border-l-2 lg:border-l-4 border-purple-500'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <span className="mr-1 sm:mr-2 text-sm">{section.icon}</span>
                        <span className="truncate">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 space-y-6 lg:space-y-8">
              {/* Header Section */}
              <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-xl border border-gray-200 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-20 translate-x-20"></div>
                <div className="relative">
                  <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-600 text-white p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                      <svg className="w-5 h-5 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                        Privacy Policy
                      </h1>
                      <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">SANIBIZ MIS - Your privacy and data protection rights</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 bg-purple-50 px-6 py-3 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0V6a2 2 0 012-2h4a2 2 0 012 2v1m-6 0h6m-6 0v12a2 2 0 002 2h4a2 2 0 002-2V7m-6 0V6" />
                      </svg>
                      <span className="text-purple-900 font-semibold">Effective Date:</span>
                    </div>
                    <span className="text-purple-700 font-medium">March 1, 2025</span>
                  </div>
                </div>
              </div>

              {/* Section 1: Introduction */}
              <section id="introduction" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-blue-100 text-blue-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">🔒</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Introduction</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">Our commitment to protecting your privacy</p>
                  </div>
                </div>
                <div className="ml-16">
                  <div className="bg-purple-50 p-6 rounded-xl border-l-4 border-purple-500">
                    <p className="text-gray-700 leading-relaxed">
                      Welcome to <span className="font-semibold text-purple-700">Star Solutions</span> ("Company", "we", "our", "us"). 
                      We are committed to protecting your privacy and ensuring that your personal information is handled securely. 
                      This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
                      SANIBIZ MIS web application (specialized for tiles and sanitary products) and Tally Maestro mobile app (collectively, the "Service"), 
                      including our integration with Tally Prime 6.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2: Information We Collect */}
              <section id="information-collect" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-green-100 text-green-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">📊</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Information We Collect</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">Types of data we process</p>
                  </div>
                </div>
                <div className="ml-16 space-y-6">
                  <div className="grid gap-6">
                    <div className="bg-blue-50 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-blue-200">
                      <div className="flex items-start space-x-3 sm:space-x-4">
                        <div className="bg-blue-200 p-2 sm:p-3 rounded-lg flex-shrink-0">
                          <span className="text-lg sm:text-2xl">📝</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2 sm:mb-3">Personal Information</h3>
                          <div className="space-y-2">
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Employee name and contact details</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <span className="text-gray-700">Email address and phone number</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <span className="text-gray-700">Business details and company registration information</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <span className="text-gray-700">Role and access permissions within your organization</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-green-200">
                      <div className="flex items-start space-x-3 sm:space-x-4">
                        <div className="bg-green-200 p-2 sm:p-3 rounded-lg flex-shrink-0">
                          <span className="text-lg sm:text-2xl">📈</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-semibold text-green-900 mb-2 sm:mb-3">Usage Data</h3>
                          <div className="space-y-2">
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">IP address and browser information</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Device information and operating system</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Application usage patterns and feature access logs</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">QR scan activity and item search history</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-orange-50 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-orange-200">
                      <div className="flex items-start space-x-3 sm:space-x-4">
                        <div className="bg-orange-200 p-2 sm:p-3 rounded-lg flex-shrink-0">
                          <span className="text-lg sm:text-2xl">💼</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-semibold text-orange-900 mb-2 sm:mb-3">Business Data</h3>
                          <div className="space-y-2">
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Tiles and sanitary product inventory and stock information</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Tally Prime 6 synchronization data and financial records</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Transaction records, delivery orders, and invoices</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Company logos, product images, and documents stored via GridFS</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-purple-50 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-purple-200">
                      <div className="flex items-start space-x-3 sm:space-x-4">
                        <div className="bg-purple-200 p-2 sm:p-3 rounded-lg flex-shrink-0">
                          <span className="text-lg sm:text-2xl">🍪</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg sm:text-xl font-semibold text-purple-900 mb-2 sm:mb-3">Cookies & Authentication Tokens</h3>
                          <div className="space-y-2">
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">JWT authentication tokens with 7-day retention for secure access</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Session cookies for maintaining login state and user preferences</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Analytics cookies for performance monitoring and usage insights</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Functional cookies for feature preferences and customization settings</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-xs sm:text-sm">Cookie consent tracking to remember your privacy choices</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3: How We Use Your Information */}
              <section id="how-we-use" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-orange-100 text-orange-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">🚀</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">How We Use Your Information</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">Purpose and legal basis for data processing</p>
                  </div>
                </div>
                <div className="ml-0 sm:ml-8 lg:ml-16">
                  <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-blue-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                        <div className="bg-blue-500 text-white p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                          <span className="text-sm sm:text-lg">🚀</span>
                        </div>
                        <h3 className="text-base sm:text-lg font-semibold text-blue-900">Service Operations</h3>
                      </div>
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                        Provide, operate, and maintain SANIBIZ MIS and Tally Maestro to deliver comprehensive ERP functionality for tiles and sanitary products, including inventory management, Tally Prime 6 synchronization, and multi-company operations.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="bg-purple-500 text-white p-2 rounded-lg">
                          <span className="text-lg">💡</span>
                        </div>
                        <h3 className="text-lg font-semibold text-purple-900">User Experience</h3>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Improve and enhance our applications based on user feedback, usage patterns, and performance metrics to optimize workflows and functionality.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="bg-green-500 text-white p-2 rounded-lg">
                          <span className="text-lg">📨</span>
                        </div>
                        <h3 className="text-lg font-semibold text-green-900">Communications</h3>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Send system updates, security alerts, support messages, and important notifications related to your account and service usage.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl border border-red-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="bg-red-500 text-white p-2 rounded-lg">
                          <span className="text-lg">🛡️</span>
                        </div>
                        <h3 className="text-lg font-semibold text-red-900">Security & Compliance</h3>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Ensure security through JWT authentication, prevent fraud, monitor for unauthorized access, and maintain data integrity across multi-tenant architecture.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl border border-yellow-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="bg-yellow-500 text-white p-2 rounded-lg">
                          <span className="text-lg">🔄</span>
                        </div>
                        <h3 className="text-lg font-semibold text-yellow-900">Data Synchronization</h3>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Facilitate real-time Tally Prime 6 integration, background sync operations, and automated data backups to ensure business continuity and data accuracy.
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-xl border border-pink-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-3 mb-4">
                        <div className="bg-pink-500 text-white p-2 rounded-lg">
                          <span className="text-lg">📊</span>
                        </div>
                        <h3 className="text-lg font-semibold text-pink-900">Analytics & Reporting</h3>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Generate comprehensive reports, track inventory movements, analyze stock patterns, and provide business insights for decision-making.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 4: Contact Us */}
              <section id="contact" className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start space-x-3 sm:space-x-4 mb-4 sm:mb-6">
                  <div className="bg-purple-100 text-purple-700 p-2 sm:p-3 rounded-lg sm:rounded-xl flex-shrink-0">
                    <span className="text-lg sm:text-2xl">📮</span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Contact Us</h2>
                    <p className="text-sm sm:text-base text-gray-600 mt-1">How to reach us regarding privacy matters</p>
                  </div>
                </div>
                <div className="ml-0 sm:ml-8 lg:ml-16">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 sm:p-6 rounded-lg sm:rounded-xl border border-purple-100">
                    <p className="text-gray-700 mb-4 sm:mb-6 font-medium text-sm sm:text-base">
                      If you have any questions about this Privacy Policy or our data practices, please contact us.
                      For detailed information about cookies, please see our{' '}
                      <Link to="/cookie-policy" className="text-purple-600 font-semibold underline hover:text-purple-700">
                        Cookie Policy
                      </Link>.
                    </p>
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-start space-x-3 sm:space-x-4 bg-white/80 p-3 sm:p-4 rounded-lg border border-purple-200">
                        <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                          <span className="text-lg sm:text-xl">🏢</span>
                        </div>
                        <div className="text-gray-800 min-w-0">
                          <p className="font-semibold mb-1 text-sm sm:text-base">Star Solutions</p>
                          <p className="text-xs sm:text-sm text-gray-600">Niraj Maheswari</p>
                          <p className="text-xs sm:text-sm text-gray-600 mt-2">
                            84, Bylane No 6, Borthakur Mill Road,<br/>
                            Ulubari, Guwahati - 781007,<br/>
                            Assam, India
                          </p>
                        </div>
                      </div>
                      <a 
                        href="mailto:info@starsolutions.biz" 
                        className="flex items-center space-x-3 sm:space-x-4 bg-white/80 p-3 sm:p-4 rounded-lg hover:bg-white transition-colors shadow-sm border border-purple-200"
                      >
                        <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                          <span className="text-lg sm:text-xl">📧</span>
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium underline text-purple-600 text-sm sm:text-base break-all">info@starsolutions.biz</span>
                          <p className="text-xs sm:text-sm text-gray-600">Email us for privacy-related questions</p>
                        </div>
                      </a>
                      <a 
                        href="tel:+918133047257" 
                        className="flex items-center space-x-3 sm:space-x-4 bg-white/80 p-3 sm:p-4 rounded-lg hover:bg-white transition-colors shadow-sm border border-purple-200"
                      >
                        <div className="bg-purple-100 p-2 rounded-lg flex-shrink-0">
                          <span className="text-lg sm:text-xl">📱</span>
                        </div>
                        <div className="min-w-0">
                          <span className="font-medium text-gray-800 text-sm sm:text-base">+91 8133047257</span>
                          <p className="text-xs sm:text-sm text-gray-600">Call us for immediate assistance</p>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
              </section>

              {/* Data Protection Notice */}
              <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-dashed border-gray-300 text-center">
                <div className="max-w-2xl mx-auto">
                  <div className="bg-amber-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl">🔐</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Data Protection & Updates</h3>
                  <p className="text-gray-700 mb-4">
                    We implement industry-standard security measures including JWT authentication, encrypted storage, 
                    role-based access control, and automated daily backups to protect your data. We reserve the right 
                    to update this privacy policy periodically to reflect changes in our practices or legal requirements.
                  </p>
                  <div className="mt-4 text-sm text-gray-500">
                    Last updated: March 1, 2025
                  </div>
                </div>
              </div>

              {/* Navigation Footer */}
              <div className="flex justify-between items-center bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                <Link
                  to="/"
                  className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Back to Home</span>
                </Link>
                <Link
                  to="/terms-of-use"
                  className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <span>Terms of Use</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
